/**
 * Canonical venue resolution for events.
 * Single Source of Truth — all surfaces MUST use this.
 *
 * Resolution order (custom wins):
 * 1. custom_address (when use_producer_address is false)
 * 2. Producer name/city (when use_producer_address is true)
 * 3. Fallback: "Plats ej angiven"
 *
 * @see docs/design-rules/events/venue-resolution.md
 */

export type VenueKind = 'custom' | 'producer_address' | 'none';

export interface VenueResolution {
  venue_text: string;
  venue_kind: VenueKind;
}

export interface VenueInput {
  /** Whether the event uses the producer's address */
  use_producer_address?: boolean | null;
  /** Custom address text */
  custom_address?: string | null;
  /** Producer name (fallback) */
  producer_name?: string | null;
  /** Producer city (optional suffix) */
  producer_city?: string | null;
}

const FALLBACK = 'Plats ej angiven';

/**
 * Resolve the canonical venue text for an event.
 */
export function resolveVenueText(input: VenueInput): VenueResolution {
  const useProdAddr = input.use_producer_address ?? true;

  // 1. Custom address wins when use_producer_address is false
  if (!useProdAddr && input.custom_address?.trim()) {
    return {
      venue_text: input.custom_address.trim(),
      venue_kind: 'custom',
    };
  }

  // 2. Producer address (name + optional city)
  if (useProdAddr && input.producer_name?.trim()) {
    const city = input.producer_city?.trim();
    return {
      venue_text: `Hos ${input.producer_name.trim()}${city ? `, ${city}` : ''}`,
      venue_kind: 'producer_address',
    };
  }

  // 2b. Producer city only (when name is omitted, e.g. EventDetailPage separates host/venue)
  if (useProdAddr && input.producer_city?.trim()) {
    return {
      venue_text: input.producer_city.trim(),
      venue_kind: 'producer_address',
    };
  }

  // Edge case: use_producer_address is false but no custom_address provided
  // Fall back to producer name if available
  if (input.producer_name?.trim()) {
    return {
      venue_text: `Hos ${input.producer_name.trim()}`,
      venue_kind: 'producer_address',
    };
  }

  // 3. Fallback
  return {
    venue_text: FALLBACK,
    venue_kind: 'none',
  };
}
